import {
	ActionButton,
	AlertDialog,
	Badge,
	Checkbox,
	DialogTrigger,
	Heading,
	Tab,
	TabList,
	TabPanel,
	Tabs,
} from '@react-spectrum/s2';
import ChevronLeft from '@react-spectrum/s2/icons/ChevronLeft';
import Delete from '@react-spectrum/s2/icons/Delete';
import Refresh from '@react-spectrum/s2/icons/Refresh';
import { style } from '@react-spectrum/s2/style' with { type: 'macro' };
import { useState } from 'react';
import type { Key } from 'react-aria-components';

import type { Strategy } from '../../../../bridge/types/strategy.ts';

import { funnelLabel, funnelVariant, timeAgo } from './strategyFormat';
import {
	AudienceSection,
	CampaignsSection,
	MetricsSection,
	type CampaignBuildRequest,
} from './StrategyPlanSections';
import { useStrategyPlanView } from './useStrategyPlanView';

type BadgeVariant = React.ComponentProps<typeof Badge>['variant'];

const SECTION_TABS = [
	{ id: 'campaigns', label: 'Campaigns' },
	{ id: 'audience', label: 'Audience' },
	{ id: 'metrics', label: "How we'll measure it" },
] as const;

export function StrategyDetailView({
	run,
	onBack,
	onDelete,
	onGenerateAgain,
	onActivate,
	onActivationConflict,
	onViewSetup,
}: {
	run: Strategy;
	onBack?: () => void;
	onDelete: () => Promise<void>;
	onGenerateAgain: () => void | Promise<void>;
	onActivate: CampaignBuildRequest;
	onActivationConflict?: () => Promise<void>;
	onViewSetup: (processId: string) => void;
}) {
	const [showProv, setShowProv] = useState(false);
	const [deleteError, setDeleteError] = useState<string | null>(null);
	const [tab, setTab] = useState<Key>('campaigns');

	const view = useStrategyPlanView(run);
	const { strategy, funnelStages } = view;

	return (
		<Tabs
			selectedKey={tab}
			onSelectionChange={setTab}
			aria-label="Strategy sections"
			UNSAFE_className="flex min-h-0 flex-1 flex-col overflow-hidden"
			styles={style({ flexShrink: 1, minHeight: 0 })}
		>
			<div className="flex-shrink-0 space-y-5 pb-3">
				<div
					data-testid="strategy-detail-eyebrow-row"
					className="flex items-center justify-between gap-4"
				>
					<div className="flex min-w-0 items-center gap-1">
						{onBack && (
							<ActionButton isQuiet aria-label="Back" onPress={onBack}>
								<ChevronLeft />
							</ActionButton>
						)}
						<span className="text-sm text-(--text-muted)">Strategy</span>
					</div>
					<div className="flex shrink-0 items-center gap-1">
						<Checkbox isSelected={showProv} onChange={setShowProv}>
							Show sources
						</Checkbox>
						<ActionButton
							isQuiet
							aria-label="Generate again"
							onPress={onGenerateAgain}
						>
							<Refresh />
						</ActionButton>
						<DialogTrigger>
							<ActionButton isQuiet aria-label="Delete">
								<Delete />
							</ActionButton>
							<AlertDialog
								variant="destructive"
								title="Delete this strategy?"
								primaryActionLabel="Delete"
								cancelLabel="Cancel"
								autoFocusButton="cancel"
								onPrimaryAction={async () => {
									setDeleteError(null);
									try {
										await onDelete();
									} catch (err) {
										setDeleteError(
											err instanceof Error
												? err.message
												: "We couldn't delete this strategy.",
										);
									}
								}}
							>
								This permanently deletes this strategy, so you can start fresh.
								This can&apos;t be undone.
							</AlertDialog>
						</DialogTrigger>
					</div>
				</div>

				<Heading
					level={1}
					styles={style({
						font: 'heading',
						fontWeight: 'bold',
						fontSize: 'heading-lg',
					})}
				>
					{strategy?.wish?.outcome ?? 'Your strategy'}
				</Heading>

				{(funnelStages.length > 0 || run.started_at) && (
					<div className="flex flex-wrap items-center gap-x-3 gap-y-2">
						{funnelStages.length > 0 && (
							<div className="flex flex-wrap gap-1.5">
								{funnelStages.map((s) => (
									<Badge
										key={s}
										variant={funnelVariant(s) as BadgeVariant}
										size="S"
										fillStyle="subtle"
									>
										{funnelLabel(s)}
									</Badge>
								))}
							</div>
						)}
						<p className="text-sm text-(--text-muted)">
							Generated {timeAgo(run.completed_at ?? run.started_at)}
						</p>
					</div>
				)}

				{deleteError && <p className="text-sm text-red-600">{deleteError}</p>}

				<TabList>
					{SECTION_TABS.map((t) => (
						<Tab key={t.id} id={t.id}>
							{t.label}
						</Tab>
					))}
				</TabList>
			</div>

			<div className="scrollbar-hidden min-h-0 flex-1 overflow-y-auto pt-5 pb-8">
				<TabPanel id="campaigns">
					<section className="space-y-4">
						{/* A summary per campaign: title, aim, status. Reading a campaign in
						    depth — what it built, and the plan behind it — belongs to the
						    review panel, which is where the owner is actually deciding. This
						    list is for seeing where each one stands. */}
						<CampaignsSection
							run={run}
							view={view}
							showProv={showProv}
							variant="summary"
							campaignActivation={{
								strategyId: run.id,
								onActivate,
								onActivationConflict,
								onViewSetup,
							}}
						/>
					</section>
				</TabPanel>

				<TabPanel id="audience">
					<section className="space-y-4">
						<AudienceSection view={view} showProv={showProv} />
					</section>
				</TabPanel>

				<TabPanel id="metrics">
					<section className="space-y-4">
						<MetricsSection run={run} view={view} showProv={showProv} />
					</section>
				</TabPanel>
			</div>
		</Tabs>
	);
}
